/**
 * Contract Verification Utility
 * Verifies contract addresses and RPC connectivity on app startup
 */

import { MEZO_CONTRACTS } from '@/lib/mezo';
import { createPublicClient, http, type PublicClient } from 'viem';
import { mezoTestnet } from '@/main';

let publicClient: PublicClient | null = null;

export function getPublicClient(): PublicClient {
  if (!publicClient) {
    publicClient = createPublicClient({
      chain: mezoTestnet,
      transport: http('https://rpc.test.mezo.org'),
    });
  }
  return publicClient;
}

export async function verifyContractAddress(contractAddress: string, contractName: string): Promise<boolean> {
  try {
    const client = getPublicClient();
    
    // Try to read from the contract (any read will work)
    const code = await client.getBytecode({ address: contractAddress as `0x${string}` });
    
    if (!code || code === '0x') {
      console.error(`❌ Contract ${contractName} at ${contractAddress} has no code - contract may not be deployed`);
      return false;
    }
    
    console.log(`✅ Contract ${contractName} verified at ${contractAddress}`);
    return true;
  } catch (error: any) {
    console.error(`❌ Failed to verify contract ${contractName} at ${contractAddress}:`, error.message);
    return false;
  }
}

export async function testRPCConnection(): Promise<boolean> {
  try {
    const client = getPublicClient();
    const blockNumber = await client.getBlockNumber();
    console.log(`✅ RPC connection successful - Current block: ${blockNumber}`);
    return true;
  } catch (error: any) {
    console.error('❌ RPC connection failed:', error.message);
    console.error('   RPC URL: https://rpc.test.mezo.org');
    return false;
  }
}

export async function verifyAllContracts(): Promise<void> {
  console.log('🔍 Verifying contracts and RPC connection...');
  
  // Test RPC first
  const rpcOk = await testRPCConnection();
  if (!rpcOk) {
    console.error('❌ RPC connection failed - contract queries will not work');
    return;
  }
  
  // Verify each contract
  await verifyContractAddress(MEZO_CONTRACTS.INVOICE_CONTRACT, 'INVOICE_CONTRACT');
  await verifyContractAddress(MEZO_CONTRACTS.MEZO_VAULT, 'MEZO_VAULT');
  await verifyContractAddress(MEZO_CONTRACTS.MUSD_TOKEN, 'MUSD_TOKEN');
  
  console.log('✅ Contract verification complete');
}
